import { requestJson, type ApiEnvelope } from '@shared/api/http';

export type FileTicketRequest = {
  taskId: string;
  fileRole: number;
  deviceId: string;
};

export type FileTicketResponse = {
  ticket: string;
  expiresAt?: number;
};

export type FileTransferLifecycleSnapshot = {
  transferId: string;
  fileId: string;
  fromUserId: number;
  toUserId: number;
  mode: string;
  state: 'offered' | 'claimed' | 'streaming' | 'reconnecting' | 'completed' | 'failed' | 'expired' | 'cancelled';
  version: number;
  offerExpiresAt: number;
  terminalCause: string;
  updatedAt: number;
};

export type FileTransferSyncChange = {
  cursor: number;
  projection: FileTransferLifecycleSnapshot;
};

export type FileTransferSyncResponse = {
  nextCursor: number;
  hasMore: boolean;
  resetRequired: boolean;
  changes: FileTransferSyncChange[];
};

/**
 * Fetch a server-issued, scoped file WebSocket ticket.
 *
 * This deliberately does not construct or sign tickets in the browser. The
 * route is a contract for the future authenticated BFF issuer and must only
 * be injected into FileTransferManager once that issuer is deployed.
 */
export async function requestFileTicket(
  baseUrl: string,
  input: FileTicketRequest,
): Promise<FileTicketResponse> {
  const response = await requestJson<ApiEnvelope<FileTicketResponse>>('/file-transfers/ticket', {
    baseUrl,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const ticket = response.data?.ticket;
  if (!ticket || !/^v1\.[0-9]+\.[^.]+\.[12]\.[^.]+\.[a-f0-9]{32}\.[0-9]+\.[a-f0-9]{64}$/.test(ticket)) {
    throw new Error('服务端未返回文件传输授权票据');
  }
  return response.data as FileTicketResponse;
}

export async function requestFileTransferSnapshot(
  baseUrl: string,
  transferId: string,
): Promise<FileTransferLifecycleSnapshot> {
  const response = await requestJson<ApiEnvelope<FileTransferLifecycleSnapshot>>('/file-transfers/snapshot', {
    baseUrl,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ transferId }),
  });
  const snapshot = response.data;
  if (!snapshot || snapshot.transferId !== transferId || !snapshot.fileId ||
      !['offered', 'claimed', 'streaming', 'reconnecting', 'completed', 'failed', 'expired', 'cancelled'].includes(snapshot.state)) {
    throw new Error('服务端未返回有效的文件传输状态');
  }
  return snapshot;
}

export async function requestFileTransferProjections(
  baseUrl: string,
  transferIds: string[],
): Promise<FileTransferLifecycleSnapshot[]> {
  const response = await requestJson<ApiEnvelope<{ projections?: FileTransferLifecycleSnapshot[] }>>('/file-transfers/projections', {
    baseUrl,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ transferIds }),
  });
  return (response.data?.projections ?? []).filter((snapshot) => snapshot.transferId && snapshot.fileId);
}

export async function requestFileTransferSync(
  baseUrl: string,
  afterCursor: number,
  limit = 200,
): Promise<FileTransferSyncResponse> {
  const response = await requestJson<ApiEnvelope<FileTransferSyncResponse>>('/file-transfers/sync', {
    baseUrl,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ afterCursor, limit }),
  });
  const data = response.data;
  if (!data || !Number.isSafeInteger(data.nextCursor) || !Array.isArray(data.changes)) {
    throw new Error('服务端未返回有效的文件传输同步状态');
  }
  return data;
}
